import { Button } from '@cloudflare/kumo';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Node, Edge } from 'reactflow';
import { answerCondition, runFrom, SAMPLE_CONTACT, SimItem, startFlow } from '../lib/flowSimulator';
import { ICONS, Glyph } from './ui/icons';

interface FlowPreviewModalProps {
  nodes: Node[];
  edges: Edge[];
  onClose: () => void;
}

type Bubble = SimItem & { id: number; from: 'bot' | 'user' | 'system'; at: string };

const MEDIA_LABEL = { image: 'Imagem', video: 'Vídeo', audio: 'Áudio', document: 'Arquivo' } as const;
const now = () => new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

function BotContent({ item }: { item: SimItem }) {
  const [broken, setBroken] = useState(false);
  if (item.kind === 'text') return <p>{item.text}</p>;
  if (item.kind === 'buttons') {
    return (
      <>
        <p>{item.text}</p>
        {item.footer && <small className="flow-sim__footer">{item.footer}</small>}
      </>
    );
  }
  if (item.kind === 'media') {
    const url = broken ? undefined : item.url;
    const media = url && item.media === 'image' ? <img src={url} alt="" onError={() => setBroken(true)} />
      : url && item.media === 'video' ? <video src={url} controls preload="metadata" onError={() => setBroken(true)} />
      : url && item.media === 'audio' ? <audio src={url} controls preload="metadata" onError={() => setBroken(true)} />
      : (
        <div className="flow-sim__file">
          <Glyph icon={item.media === 'document' ? ICONS.File : ICONS.Warning} size={16}/>
          <span>{item.media === 'document' ? (item.fileName || 'documento.pdf') : `${MEDIA_LABEL[item.media]} ${item.url ? 'indisponível' : 'sem arquivo'}`}</span>
        </div>
      );
    return (
      <>
        <div className="flow-sim__media">{media}</div>
        {item.caption && <p>{item.caption}</p>}
      </>
    );
  }
  return null;
}

/** Preview do fluxo interativo: conversa simulada com as regras do disparo real */
export function FlowPreviewModal({ nodes, edges, onClose }: FlowPreviewModalProps) {
  const [bubbles, setBubbles] = useState<Bubble[]>([]);
  const [waitingAt, setWaitingAt] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [input, setInput] = useState('');
  const seq = useRef(0);
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const push = useCallback((items: SimItem[], from?: Bubble['from']) => {
    setBubbles((prev) => [...prev, ...items.map((it) => ({ ...it, id: ++seq.current, at: now(), from: from ?? (it.kind === 'note' ? 'system' : 'bot') }))]);
  }, []);

  const restart = useCallback(() => {
    seq.current = 0;
    setBubbles([]);
    const step = startFlow(nodes, edges);
    push(step.items);
    setWaitingAt(step.waitingAt);
    setSuggestions(step.suggestions);
    setInput('');
  }, [nodes, edges, push]);

  // Reinicia só ao abrir: editar o fluxo com o preview aberto não apaga a conversa
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { restart(); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [bubbles]);

  const reply = (text: string) => {
    const answer = text.trim();
    if (!answer || !waitingAt) return;
    push([{ kind: 'text', text: answer }], 'user');
    setInput('');
    const node = nodes.find((n) => n.id === waitingAt);
    if (!node) { setWaitingAt(null); return; }
    const { target, path } = answerCondition(node, edges, answer);
    const items: SimItem[] = [{ kind: 'note', text: `Condição: caminho “${path}”.` }];
    if (!target) {
      items.push({ kind: 'note', tone: 'warn', text: `A saída “${path}” da condição não está ligada a nenhum bloco.` });
      push(items);
      setWaitingAt(null); setSuggestions([]);
      return;
    }
    const step = runFrom(node.id, nodes, edges, target);
    push([...items, ...step.items]);
    setWaitingAt(step.waitingAt);
    setSuggestions(step.suggestions);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const lastButtons = [...bubbles].reverse().find((b) => b.kind === 'buttons') as (Bubble & { kind: 'buttons' }) | undefined;
  const quick = waitingAt
    ? [...(lastButtons?.options ?? []), ...suggestions].filter((q, i, all) => all.findIndex((o) => o.toLowerCase() === q.toLowerCase()) === i).slice(0, 8)
    : [];

  return (
    <aside className="flow-sim" aria-label="Preview do fluxo">
      <header className="flow-sim__header">
        <div className="flow-sim__avatar" aria-hidden><ICONS.Person size={16} /></div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <strong>{SAMPLE_CONTACT.nome} (contato de exemplo)</strong>
          <small>Simulação: nada é enviado</small>
        </div>
        <Button variant="ghost" shape="square" icon={ICONS.Refresh} aria-label="Reiniciar conversa" title={'Reiniciar conversa'} onClick={restart} />
        <Button variant="ghost" shape="square" icon={ICONS.X} aria-label="Fechar preview" onClick={onClose} />
      </header>

      <div className="flow-sim__chat" aria-live="polite">
        {bubbles.map((b) => (
          b.from === 'system' ? (
            <div key={b.id} className={`flow-sim__note flow-sim__note--${(b.kind === 'note' && b.tone) || 'info'}`}>{b.kind === 'note' && b.text}</div>
          ) : (
            <div key={b.id} className={`flow-sim__row flow-sim__row--${b.from}`}>
              <div className="flow-sim__bubble">
                {b.from === 'user' && b.kind === 'text' ? <p>{b.text}</p> : <BotContent item={b} />}
                <time>{b.at}</time>
              </div>
              {b.kind === 'buttons' && b.options.length > 0 && (
                <div className="flow-sim__options">
                  {b.options.map((o, i) => (
                    <button key={i} type="button" disabled={!waitingAt} onClick={() => reply(o)}>{o}</button>
                  ))}
                </div>
              )}
            </div>
          )
        ))}
        <div ref={endRef} />
      </div>

      <footer className="flow-sim__composer">
        {quick.length > 0 && (
          <div className="flow-sim__quick" aria-label="Respostas sugeridas">
            {quick.map((q) => <button key={q} type="button" onClick={() => reply(q)}>{q}</button>)}
          </div>
        )}
        {waitingAt ? (
          <form className="flow-sim__form" onSubmit={(e) => { e.preventDefault(); reply(input); }}>
            <input ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} placeholder="Responda como o contato..." aria-label="Resposta do contato" autoFocus />
            <Button type="submit" variant="ghost" shape="square" icon={ICONS.ArrowRight} aria-label="Enviar" disabled={!input.trim()} />
          </form>
        ) : (
          <button type="button" className="flow-sim__restart" onClick={restart}><ICONS.Refresh size={16} /> Testar de novo</button>
        )}
      </footer>
    </aside>
  );
}
